import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';

/**
 * Hook to fetch page metadata by slug and automatically update the document title.
 * @param slug The slug of the page to fetch metadata for.
 * @returns The metadata query result.
 */
export const usePageMetadata = (slug: string) => {
    const { i18n, t } = useTranslation();

    const query = useQuery({
        queryKey: ['publicPage', slug, i18n.language],
        queryFn: async () => {
            const response = await apiClient.get(`/public/pages/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    useEffect(() => {
        if (query.data?.title) {
            document.title = query.data.title;
        } else if (!query.isLoading && !query.data) {
            // Fallback for document title if no data is found
            const fallbackTitle = t(`page_title_${slug}`, slug.charAt(0).toUpperCase() + slug.slice(1));
            document.title = `${fallbackTitle} | Mosaik e.V.`;
        }
    }, [query.data, query.isLoading, slug, t]);

    return query;
};
